import { trackTikTokEvent } from './tiktokTracking';

let gaInitialized = false;
let metaInitialized = false;
let lastTrackedPath = '';
const CONSENT_KEY = 'neuralia369_cookies';

const getCookieValue = (name) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=')[1]) : null;
};

const setCookieValue = (name, value, days = 365) => {
  if (typeof document === 'undefined') return;
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
};

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

export const hasTrackingConsent = () => {
  if (typeof window === 'undefined') return false;
  return getCookieValue(CONSENT_KEY) === 'accepted';
};

export const setTrackingConsent = (value) => setCookieValue(CONSENT_KEY, value);

const ensureScript = (id, src) => {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
};

export const initGoogleAnalytics = () => {
  if (gaInitialized || !GA_ID) return;

  ensureScript('ga4-script', `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(){ window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { send_page_view: false });
  gaInitialized = true;
};

export const initMetaPixel = () => {
  if (metaInitialized || !META_PIXEL_ID) return;

  (function(f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

  window.fbq('init', META_PIXEL_ID);
  metaInitialized = true;
};

export const initTracking = () => {
  if (!hasTrackingConsent()) return false;
  initGoogleAnalytics();
  initMetaPixel();
  return true;
};

export const trackPageView = (path) => {
  if (!hasTrackingConsent()) return;
  if (!path || lastTrackedPath === path) return;

  if (window.gtag && GA_ID) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: document.title,
      page_location: window.location.href,
    });
  }

  if (window.fbq && META_PIXEL_ID) {
    window.fbq('track', 'PageView');
  }
  trackTikTokEvent('PageView');

  lastTrackedPath = path;
};

export const trackLeadEvent = ({ source = 'contact_form' } = {}) => {
  if (!hasTrackingConsent()) return;
  if (window.gtag && GA_ID) {
    window.gtag('event', 'generate_lead', { source });
  }
  if (window.fbq && META_PIXEL_ID) {
    window.fbq('track', 'Lead', { content_name: source });
  }
  trackTikTokEvent('SubmitForm', { content_name: source });
};

export const trackBookingEvent = ({ bookingType, date, hour } = {}) => {
  if (!hasTrackingConsent()) return;
  if (window.gtag && GA_ID) {
    window.gtag('event', 'booking_confirmed', {
      booking_type: bookingType,
      booking_date: date,
      booking_hour: hour,
    });
  }
  if (window.fbq && META_PIXEL_ID) {
    window.fbq('trackCustom', 'BookingConfirmed', {
      booking_type: bookingType,
      booking_date: date,
      booking_hour: hour,
    });
  }
  trackTikTokEvent('Schedule', { booking_type: bookingType, booking_date: date, booking_hour: hour });
};

export const trackBeginCheckoutEvent = ({ packageId, packageName, value, currency = 'USD' }) => {
  if (!hasTrackingConsent()) return;
  if (window.gtag && GA_ID) {
    window.gtag('event', 'begin_checkout', {
      currency,
      value,
      items: [{ item_id: packageId, item_name: packageName, price: value, quantity: 1 }],
    });
  }
  if (window.fbq && META_PIXEL_ID) {
    window.fbq('track', 'InitiateCheckout', {
      content_ids: [packageId],
      content_name: packageName,
      content_type: 'product',
      currency,
      value,
    });
  }
  trackTikTokEvent('InitiateCheckout', { content_id: packageId, content_name: packageName, currency, value });
};

export const trackPurchaseEvent = ({ sessionId, packageId, packageName, value, currency = 'USD' }) => {
  if (!hasTrackingConsent()) return;
  const storageKey = `purchase-tracked:${sessionId}`;
  if (!sessionId || window.sessionStorage.getItem(storageKey)) return;

  if (window.gtag && GA_ID) {
    window.gtag('event', 'purchase', {
      transaction_id: sessionId,
      currency,
      value,
      items: [{ item_id: packageId, item_name: packageName, price: value, quantity: 1 }],
    });
  }

  if (window.fbq && META_PIXEL_ID) {
    window.fbq('track', 'Purchase', {
      content_ids: [packageId],
      content_name: packageName,
      content_type: 'product',
      currency,
      value,
    });
  }
  trackTikTokEvent('CompletePayment', { content_id: packageId, content_name: packageName, currency, value });

  window.sessionStorage.setItem(storageKey, 'true');
};
